import React, { FunctionComponent } from 'react';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next'

import {
  Box,
  Grid,
  Paper,
} from '@mui/material';
import {
  Switcher,
  Typography
} from '../../../../components/v2/styled';

import HelpersClientWidgets from '../../../../actions/helpers/client_widgets';

// Notification-information widget settings. Plain per-item visibility map
// under one value key (`notification_information`):
//   Record<string, boolean>  (nutri_appointment, nutri_workshop)
// Pure two-state (shown/hidden), like actions' visibility map but with no
// order and no widget-level toggles — the simplest settings shape in the
// module. The yes/no value the row reports still comes from the *client's*
// settings (object.settings.notifications.*); this setting only controls
// whether the row renders at all.
// No order setting — the 2 notifications render in their fixed natural order.

export type NotificationInformationSettingsValue = Record<string, boolean>;

type Props = {
  value: NotificationInformationSettingsValue;
  onChange: (v: NotificationInformationSettingsValue) => void;
  // Unused for this widget (no order setting). Kept in the shared
  // SettingsProps shape so the dialog renders it uniformly.
  order?: string[];
  onOrderChange?: (v: string[]) => void;
};

const LABEL_BASE = 'coach.personal_settings.userdetail_widgets.settings.notification_information';

export const CoachSettingsWidgetNotificationInformation: FunctionComponent<Props> = ({
  value,
  onChange
}) => {
  const theme = useTheme();
  const {t} = useTranslation(['translations']);

  // Defensive default so the dialog works before the first save.
  const visibility = value || {};

  // Fixed-order items, each tagged with shown/hidden state.
  const orderedItems = HelpersClientWidgets.getOrderedNotificationItems(visibility);

  return <Grid container spacing={2}>
    <Grid item xs={12}>
      <Typography variant="caption" sx={{color: 'text.secondary'}}>
        {t(`${LABEL_BASE}.description`)}
      </Typography>
    </Grid>

    {/* Notification list: pure show/hide. No order, so no drag handles. */}
    <Grid item xs={12}>
      <Typography variant="subtitle2" sx={{color: 'text.secondary'}}>
        {t(`${LABEL_BASE}.items.title`)}
      </Typography>
      <Paper variant="outlined" sx={{padding: 1, marginTop: 0.5}}>
        {orderedItems.map((item) => {
          const { Icon } = item;
          const checked = item.state === 'shown';
          const stateColor = checked
            ? theme.palette.primary.main
            : theme.palette.secondary.main;
          return (
            <Box
              key={item.key}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                padding: 1,
                borderRadius: 0.5,
                marginTop: 0.5,
              }}
            >
              <Icon sx={{fontSize: '1.2rem', color: stateColor}} />
              <Switcher
                label={t(`${LABEL_BASE}.items.${item.key}`)}
                value={checked}
                greying
                onChange={(c) => onChange({ ...visibility, [item.key]: c })}
              />
            </Box>
          );
        })}
      </Paper>
    </Grid>
  </Grid>;
}

// Read-only status: notification icons coloured by shown/hidden so the placed
// box summarises the config at a glance.
type StatusProps = {
  value: NotificationInformationSettingsValue;
  order?: string[];
};

export const CoachSettingsWidgetNotificationInformationStatus: FunctionComponent<StatusProps> = ({
  value
}) => {
  const theme = useTheme();
  const {t} = useTranslation(['translations']);

  const visibility = value || {};
  const orderedItems = HelpersClientWidgets.getOrderedNotificationItems(visibility);

  return <Box sx={{display: 'flex', flexDirection: 'column', gap: 0.5, marginTop: 1}}>
    <Box sx={{display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1.5}}>
      {orderedItems.map((item) => {
        const { Icon } = item;
        const color = item.state === 'shown'
          ? theme.palette.primary.main
          : theme.palette.secondary.main;
        return <Box
          key={item.key}
          title={t(`${LABEL_BASE}.items.${item.key}`)}
        >
          <Icon sx={{ fontSize: '1.1rem', color }} />
        </Box>;
      })}
    </Box>
  </Box>;
}
